import { Storage } from "aws-amplify";
import { StatusTextMap } from "components/parts/Label/StatusLabel/StatusLabel";
import { decideInterviewStatus } from "pages/InterviewManagement/hook";
import {
  decideSubjectStatus,
  returnSubjectDate,
} from "pages/SubjectManagement/hook";
import { useEffect, useState } from "react";
import { InterviewType, ResourceType } from "types/graphqlapi";
import { fetchFileResourcesBySubjectIdQuery } from "utils/apis/fileResource";
import { listInterviewsQuery } from "utils/apis/interview";
import { fetchMonitorBysubjectIdQuery } from "utils/apis/monitor";
import { fetchMonitorAssignBysubjectIdQuery } from "utils/apis/monitorAssign";
import { fetchMonitorAttributeByMonitorIdQuery } from "utils/apis/monitorAttribute";
import { fetchScenarioBysubjectIdQuery } from "utils/apis/scenario";
import { getSubjectQuery } from "utils/apis/subject";
import { getAdminUserQuery, getUserByIdQuery } from "utils/apis/user";
import { InterviewTypeTextMapper } from "utils/common";

type SubjectInformation = {
  subjectNumber: string;
  subjectName: string;
  interviewTypeDescription: string;
  subjectDescription: string;
  subjectPurpose: string;
  mainUserGroup: string;
  mainUserAsmarq: string;
  scheduledEndDate: string;
  status: string;
  memo: string;
};

type ClientFileResource = {
  type: ResourceType;
  sortIndex: number;
  title: string;
  description: string;
  fileName: string;
  url: string;
};

type ScenarioInformation = {
  sortIndex: number;
  scenarioNo: number;
  templateNo: number;
  text: string;
};

type Attribute = {
  seqNo: number;
  name: string;
  value: string;
};

type MonitorInfomation = {
  memberId: string;
  ansId: string;
  name: string;
  classfication: string;
  sex: string;
  age: string;
  address: string;
  marriage: string;
  children: string;
  job: string;
  attribute: Attribute[];
};

type ChatRoomInformation = {
  id: string;
  interviewSeq: number;
  scheduled: string;
  actual: string;
  scheduledMonitor: string;
  actualMonitor: string;
  memo: string;
  note: string;
  status: string;
};

type InformationForExport = {
  key: number;
  label: string;
  value: string;
};

const initSubjectInformation: SubjectInformation = {
  subjectNumber: "",
  subjectName: "",
  interviewTypeDescription: "",
  subjectDescription: "",
  subjectPurpose: "",
  mainUserGroup: "",
  mainUserAsmarq: "",
  scheduledEndDate: "",
  status: "",
  memo: "",
};

const convertNullToBlank = (str: string | undefined | null) => {
  if (str === null || str === undefined) return "";
  return str;
};

const formatDateTimeToSlash = (str: string) => {
  if (str === "") return str;
  return str.replaceAll("-", "/");
};

const getUserName = async (user_id?: string | null) => {
  if (user_id === undefined || user_id === null) return "";
  const fetchedData = await getUserByIdQuery(user_id);
  if (fetchedData === null || fetchedData === undefined) return "";
  return fetchedData.name;
};

const getAdminUserName = async (user_id?: string | null) => {
  if (user_id === undefined || user_id === null) return "";
  const fetchedData = await getAdminUserQuery(user_id);
  if (fetchedData === null || fetchedData === undefined) return "";
  return fetchedData.name;
};

const getResouceFromS3 = async (url: string) => {
  const imageURL = await Storage.get(url);
  return imageURL;
};

/**
 * 案件情報取得
 * @param subjectId
 * @returns
 */
const fetchSubjectInformation = async (
  subjectId: string
): Promise<SubjectInformation> => {
  const fetchedSubjectData = await getSubjectQuery(subjectId);
  const subjectDate = await returnSubjectDate(subjectId);

  if (fetchedSubjectData === undefined)
    throw new Error("案件情報の取得に失敗しました");

  return {
    subjectNumber: fetchedSubjectData.subject_number,
    subjectName: convertNullToBlank(fetchedSubjectData.subject_name),
    interviewTypeDescription: InterviewTypeTextMapper(
      fetchedSubjectData.interview_type || InterviewType.ONE_TIME
    ),
    subjectDescription: convertNullToBlank(
      fetchedSubjectData.subject_description
    ),
    subjectPurpose: convertNullToBlank(fetchedSubjectData.subject_purpose),
    mainUserGroup: await getUserName(fetchedSubjectData.main_user_id_group),
    mainUserAsmarq: await getAdminUserName(
      fetchedSubjectData.main_user_id_asmarq
    ),
    // TODO
    scheduledEndDate: subjectDate.last_interview_date,
    status: StatusTextMap[await decideSubjectStatus(fetchedSubjectData)],
    memo: convertNullToBlank(fetchedSubjectData.memo),
  };
};

/**
 * リソース情報リスト取得
 * @param subjectId
 * @returns
 */
const fetchUsedResourse = async (
  subjectId: string
): Promise<ClientFileResource[]> => {
  const fetchedData = await fetchFileResourcesBySubjectIdQuery(subjectId);
  if (fetchedData === undefined)
    throw new Error("リソース情報の取得に失敗しました");
  const returnList: ClientFileResource[] = await Promise.all(
    fetchedData.map(async (item) => {
      return {
        type: item.type,
        sortIndex: item.sort_index,
        title: item.title,
        description: convertNullToBlank(item.description),
        fileName: item.file_name,
        url: await getResouceFromS3(item.url),
      };
    })
  );

  return returnList;
};

/**
 * インタビューフロー情報取得
 * @param subjectId
 * @returns
 */
const fetchScenarioInformation = async (
  subjectId: string
): Promise<ScenarioInformation[]> => {
  const fetchedData = await fetchScenarioBysubjectIdQuery(subjectId);
  if (fetchedData === undefined)
    throw new Error("インタビューフロー情報の取得に失敗しました");
  return fetchedData.map((item) => {
    return {
      sortIndex: item.sort_index,
      scenarioNo: item.scenario_no,
      templateNo: item.template_no,
      text: convertNullToBlank(item.text),
    };
  });
};

/**
 * インタビューフロー情報整形
 * @param scenarioInformation
 * @returns
 */
const convertScenarioInformationToExport = (
  scenarioInformation: ScenarioInformation[]
): InformationForExport[] => {
  return scenarioInformation.map((scenario) => {
    if (scenario.templateNo === 0) {
      return {
        key: scenario.sortIndex,
        label: `インタビューフロー ${scenario.scenarioNo}`,
        value: scenario.text,
      };
    } else {
      return {
        key: scenario.sortIndex,
        label: `テンプレート ${scenario.scenarioNo} - ${scenario.templateNo}`,
        value: scenario.text,
      };
    }
  });
};

/**
 * モニターアトリビュート取得
 * @param monitorId
 * @returns
 */
const fetchMonitorAttribute = async (
  monitorId: string
): Promise<Attribute[]> => {
  const fetchedData = await fetchMonitorAttributeByMonitorIdQuery(monitorId);
  if (fetchedData === undefined)
    throw new Error("モニターアトリビュート情報の取得に失敗しました");
  return fetchedData.map((item) => {
    return {
      seqNo: item.seq_no,
      name: item.name,
      value: convertNullToBlank(item.value),
    };
  });
};

/**
 * モニター情報取得
 * @param subjectId
 * @returns
 */
const fetchMonitorInformation = async (
  subjectId: string
): Promise<MonitorInfomation[]> => {
  const fetchedData = await fetchMonitorBysubjectIdQuery(subjectId);
  if (fetchedData === undefined)
    throw new Error("モニター情報の取得に失敗しました");
  const returnList: MonitorInfomation[] = await Promise.all(
    fetchedData.map(async (item) => {
      return {
        memberId: convertNullToBlank(item.member_id),
        ansId: convertNullToBlank(item.ans_id),
        name: convertNullToBlank(item.name),
        classfication: convertNullToBlank(item.classification),
        sex: convertNullToBlank(item.sex),
        age: convertNullToBlank(item.age),
        address: convertNullToBlank(item.address),
        marriage: convertNullToBlank(item.marriage),
        children: convertNullToBlank(item.children),
        job: convertNullToBlank(item.job),
        attribute: await fetchMonitorAttribute(item.id),
      };
    })
  );
  return returnList;
};

/**
 * チャットルーム情報取得
 * @param subjectId
 * @returns
 */
const fetchChatRoomInformation = async (
  subjectId: string
): Promise<ChatRoomInformation[]> => {
  const fetchedData = await listInterviewsQuery(subjectId);
  if (fetchedData === undefined)
    throw new Error("チャットルーム情報の取得に失敗しました");
  const monitorAssignList = await fetchMonitorAssignBysubjectIdQuery(subjectId);
  if (monitorAssignList === undefined)
    throw new Error("モニターアサイン情報の取得に失敗しました");
  const monitorList = await fetchMonitorBysubjectIdQuery(subjectId);
  if (monitorList === undefined)
    throw new Error("モニター情報の取得に失敗しました");

  const returnList: ChatRoomInformation[] = fetchedData.map((chatRoom) => {
    const scheduledMonitorIdList = monitorAssignList.filter(
      (monitorAssign) => monitorAssign.interview_id === chatRoom.id
    );
    const actualMonitorIdList = scheduledMonitorIdList.filter(
      (scheduledMonitor) =>
        scheduledMonitor.last_entry_date !== undefined &&
        scheduledMonitor.last_entry_date !== null
    );
    const monitorNameList = [];
    for (const scheduledMonitor of scheduledMonitorIdList) {
      for (const monitor of monitorList) {
        if (scheduledMonitor.monitor_id === monitor.id) {
          monitorNameList.push(convertNullToBlank(monitor.name));
        }
      }
    }
    const actualMonitorNameList = [];
    for (const actualMonitor of actualMonitorIdList) {
      for (const monitor of monitorList) {
        if (actualMonitor.monitor_id === monitor.id) {
          actualMonitorNameList.push(convertNullToBlank(monitor.name));
        }
      }
    }
    return {
      id: chatRoom.id,
      interviewSeq: chatRoom.interview_seq,
      scheduled: formatDateTimeToSlash(
        `${chatRoom.scheduled_start_date} ${chatRoom.scheduled_start_time}～${chatRoom.scheduled_end_date} ${chatRoom.scheduled_end_time} `
      ),
      actual: chatRoom.actual_end_date
        ? formatDateTimeToSlash(
            `${convertNullToBlank(
              chatRoom.actual_start_date
            )} ${convertNullToBlank(
              chatRoom.actual_start_time
            )}～${convertNullToBlank(
              chatRoom.actual_end_date
            )} ${convertNullToBlank(chatRoom.actual_end_time)} `
          )
        : "",
      scheduledMonitor: monitorNameList.join(" "),
      actualMonitor: actualMonitorNameList.join(" "),
      memo: convertNullToBlank(chatRoom.memo),
      note: convertNullToBlank(chatRoom.note),
      status: StatusTextMap[decideInterviewStatus(chatRoom)],
    };
  });
  return returnList.sort((a, b) => {
    if (a.interviewSeq < b.interviewSeq) return -1;
    if (a.interviewSeq > b.interviewSeq) return 1;
    return 0;
  });
};

export const useBasicInformationForReport = (subjectId: string) => {
  const [subjectInformation, setSubjectInformation] =
    useState<SubjectInformation>(initSubjectInformation);
  const [fileResource, setFileResource] = useState<ClientFileResource[]>([]);
  const [scenarioInformation, setScenarioInformation] = useState<
    InformationForExport[]
  >([]);
  const [monitorInformation, setMonitorInformation] = useState<
    MonitorInfomation[]
  >([]);
  const [chatRoomInformation, setChatRoomInformation] = useState<
    ChatRoomInformation[]
  >([]);

  const readySubjectInfomation = async () => {
    try {
      const subjectInformation = await fetchSubjectInformation(subjectId);
      setSubjectInformation(subjectInformation);
    } catch (e) {
      console.error(e);
    }
  };

  const readyUsedResouse = async () => {
    try {
      const fileResource = await fetchUsedResourse(subjectId);
      setFileResource(fileResource);
    } catch (e) {
      console.error(e);
    }
  };

  const readyScenarioInformation = async () => {
    try {
      const scenarioInformation = await fetchScenarioInformation(subjectId);
      const scenarioInformationForExport =
        convertScenarioInformationToExport(scenarioInformation);
      setScenarioInformation(scenarioInformationForExport);
    } catch (e) {
      console.error(e);
    }
  };

  const readyMonitorInformation = async () => {
    try {
      const monitorInformation = await fetchMonitorInformation(subjectId);
      setMonitorInformation(monitorInformation);
    } catch (e) {
      console.error(e);
    }
  };

  const readyChatRoomInformation = async () => {
    try {
      const chatRoomInformation = await fetchChatRoomInformation(subjectId);
      setChatRoomInformation(chatRoomInformation);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    readySubjectInfomation();
    readyUsedResouse();
    readyScenarioInformation();
    readyMonitorInformation();
    readyChatRoomInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    subjectInformation,
    fileResource,
    scenarioInformation,
    monitorInformation,
    chatRoomInformation,
  };
};
